import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Upload from 'rc-upload';
import { RcFile } from 'rc-upload/lib/interface';
import Tooltip from 'rc-tooltip';

import httpClient from '@/config/http.client';
import { useWebFormUpload } from '@/effects/use-web-form-config-effect';

import { genApiUrl } from '../../utils';

import WfIcon from './WfIcon';
import './index.scss';

const FILE_TYPE_FIRST_LINE = ['.doc', '.docx', '.dox', '.xls', '.xlsx', '.ppt', '.pptx', '.pdf', '.gif'];
const FILE_TYPE_SECOND_LINE = [
  '.jpg',
  '.jpeg',
  '.png',
  '.txt',
  '.bmp',
  '.html',
  '.htm',
  '.mp4',
  '.mp3',
  '.avi',
  '.mov',
  '.ods',
  '.csv',
];
const FILE_TYPE_WHITE_LIST = FILE_TYPE_FIRST_LINE.concat(FILE_TYPE_SECOND_LINE);
const FILE_MAX_SIZE = 10 * 1024 * 1024;
const FILE_MAX_COUNT = 10;

enum FileStatusEnum {
  Loading = 'loading',
  Resolved = 'file',
  Rejected = 'danger',
}

export interface UploadedFile {
  percent?: number;
  name: string;
  uid: string;
  id?: string;
  size: number;
  status: 'Loading' | 'Resolved' | 'Rejected';
  extension: string;
  deleteTooltipShow: boolean;
  nameTooltipShow: boolean;
  file: RcFile;
}

interface ClientError {
  name: string;
  extension: string;
  type: 'TYPE_NOT_ALLOWED' | 'OVER_SIZE' | 'OVER_COUNT';
}

type SyncTokenMap = Map<string, { resolve: (token: string) => void; reject: (reason?: any) => void }>;

const { API_GATEWAY_URL } = genApiUrl();

export interface WfUploadProps {
  isPreview?: boolean;
  uploadedFiles: UploadedFile[];
  t: (key: string) => string;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  whiteList?: string[];
  fileMaxSize?: number;
  fileMaxCount?: number;
  style?: React.CSSProperties;
}

function WfUpload(props: WfUploadProps) {
  const {
    isPreview = false,
    uploadedFiles,
    t,
    setUploadedFiles,
    whiteList = FILE_TYPE_WHITE_LIST,
    fileMaxSize = FILE_MAX_SIZE,
    fileMaxCount = FILE_MAX_COUNT,
  } = props;

  const refSyncTokenMap = useRef<SyncTokenMap>(new Map());
  const attachmentFlowIdRef = useRef('');

  const [fileUploadErrors, setFileUploadErrors] = useState<ClientError[]>([]);

  const { uploadFile, deleteFile } = useWebFormUpload();

  const getFileNameAndExtension = useCallback((rcFile: RcFile) => {
    const nameArray = rcFile.name.split('.');
    const last = nameArray.pop();
    const extension = last ? `.${last}` : '';
    const name = nameArray.join('.');
    return { extension, name };
  }, []);

  const setUploadedFilesByUid = useCallback(
    (uid: string, newData: Partial<UploadedFile>) => {
      setUploadedFiles((prev) => {
        const next = [...prev];
        const fileIndex = next.findIndex((f) => f.uid === uid);

        if (fileIndex > -1) {
          const percent = !newData.percent ? 0 : Math.max(next[fileIndex].percent || 0, newData.percent || 0);
          const newFile: UploadedFile = { ...next[fileIndex], ...newData, percent };
          next[fileIndex] = newFile;
        } else {
          const rcFile = newData.file;
          if (!rcFile) return prev;
          const { name, extension } = getFileNameAndExtension(rcFile);

          const newFile: UploadedFile = {
            uid: rcFile.uid,
            name: name,
            size: rcFile.size,
            status: 'Loading',
            extension: extension,
            deleteTooltipShow: false,
            nameTooltipShow: false,
            percent: 0,
            file: rcFile,
            ...newData,
          };
          next.push(newFile);
        }
        return next;
      });
    },
    [getFileNameAndExtension, setUploadedFiles],
  );

  const postMsg = useCallback((type: string, data?: any) => {
    if (window === window.parent) {
      alert(
        `You can't use WebForm like this.\nPlease insert 'web-form/' before 'index.html' in the url.\nExample: xxx.xxx.com/case-management-ui/latest/web-form/index.html?web_form_key=xxxxxx`,
      );
    } else {
      window.parent.postMessage(JSON.stringify({ type, data }), '*');
    }
  }, []);

  const syncToken = useCallback(() => {
    const uid = uuidv4();
    return new Promise<string>((resolve, reject) => {
      postMsg('GET_WEB_FORM_TOKEN', uid);
      refSyncTokenMap.current.set(uid, { resolve, reject });
    });
  }, [postMsg]);

  const fetchFiles = useCallback(
    async (rcFiles: RcFile[]) => {
      rcFiles.forEach((rcFile) => {
        setUploadedFilesByUid(rcFile.uid, { percent: 0, status: 'Loading', file: rcFile });
      });

      try {
        await syncToken();
      } catch (error) {
        setTimeout(() => {
          rcFiles.forEach((rcFile) => {
            setUploadedFilesByUid(rcFile.uid, { percent: 0, status: 'Rejected', file: rcFile });
          });
        }, 500);
        throw error;
      }

      rcFiles.forEach((rcFile) => {
        uploadFile(rcFile, attachmentFlowIdRef.current, (e: any) => {
          setUploadedFilesByUid(rcFile.uid, { percent: Number(e.percent) });
        })
          .then((id) => {
            setUploadedFilesByUid(rcFile.uid, { status: 'Resolved', percent: 100, id });
          })
          .catch(() => {
            setUploadedFilesByUid(rcFile.uid, { status: 'Rejected' });
          });
      });
    },
    [setUploadedFilesByUid, syncToken, uploadFile],
  );

  const validateFiles = useCallback(
    (rcFiles: RcFile[]) => {
      const newClientErrors: ClientError[] = [];
      let fileCount = uploadedFiles.length;

      const verifiedRcFiles = rcFiles.filter((rcFile) => {
        const { name, extension } = getFileNameAndExtension(rcFile);

        if (!whiteList.some((w) => RegExp(w, 'i').test(extension))) {
          newClientErrors.push({ name, extension, type: 'TYPE_NOT_ALLOWED' });
          return false;
        }
        if (rcFile.size > fileMaxSize) {
          newClientErrors.push({ name, extension, type: 'OVER_SIZE' });
          return false;
        }
        if (fileCount >= fileMaxCount) {
          newClientErrors.push({ name, extension, type: 'OVER_COUNT' });
          return false;
        }

        fileCount += 1;
        return true;
      });

      setFileUploadErrors(newClientErrors);
      return verifiedRcFiles;
    },
    [uploadedFiles.length, getFileNameAndExtension, whiteList, fileMaxSize, fileMaxCount],
  );

  const handleBatchStart = useCallback(
    async (rcFileList: { file: RcFile }[]) => {
      const rcFiles = rcFileList.map((rcFile) => rcFile.file);

      const verifiedRcFiles = validateFiles(rcFiles);
      fetchFiles(verifiedRcFiles);
    },
    [fetchFiles, validateFiles],
  );

  const handleErrorPanelClose = () => setFileUploadErrors([]);

  const handleRemoveFile = (uploadedFile: UploadedFile) => {
    const newUploadFiles = uploadedFiles.filter((f) => f.uid !== uploadedFile.uid);
    setUploadedFiles(newUploadFiles);
    if (uploadedFile.id) deleteFile(uploadedFile.id, attachmentFlowIdRef.current);
  };

  const hanldeFileClick = async (file: UploadedFile) => {
    if (file.status !== 'Rejected') return;
    fetchFiles([file.file]);
  };

  const onMessageCallback = useCallback(async (e: MessageEvent<any>) => {
    try {
      const {
        type,
        data: { token, attachmentFlowId, uid },
      } = JSON.parse(e.data);

      const syncTokenMap = refSyncTokenMap.current.get(uid);
      if (!syncTokenMap) return;

      if (attachmentFlowId) {
        attachmentFlowIdRef.current = attachmentFlowId;
      }

      switch (type) {
        case 'GET_WEB_FORM_TOKEN:SUCCESS':
          refSyncTokenMap.current.delete(uid);
          httpClient.initialize(API_GATEWAY_URL, () => Promise.resolve(token || ''));
          syncTokenMap.resolve(token);
          break;
        case 'GET_WEB_FORM_TOKEN:ERROR':
          refSyncTokenMap.current.delete(uid);
          syncTokenMap.reject();
          break;
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    window.addEventListener('message', onMessageCallback);

    return () => {
      window.removeEventListener('message', onMessageCallback);
    };
  }, [onMessageCallback]);

  return (
    <div className="wf-upload-container" style={{ marginBottom: '16px', display: 'block' }}>
      {/* Upload drag area */}
      <Upload type="drag" beforeUpload={() => false} onBatchStart={handleBatchStart} multiple disabled={isPreview}>
        <div className="wf-upload-drag-container">
          <div className="wf-upload-drag-wrap">
            <div className="wf-upload-drag-title">
              <WfIcon className="icon_upload" name="upload" />
              <span>{t('webform.upload.guide.title')}</span>
            </div>

            <div className="wf-upload-drag-info">
              <span className="wf-upload-drag-info-limit">
                {t('webform.upload.guide.description_1')}&nbsp;&nbsp;&nbsp;
              </span>

              <span className="wf-upload-drag-info-limit">{t('webform.upload.guide.description_2')}</span>

              <p className="wf-upload-drag-info-suffix">
                {t('webform.upload.guide.supported_files')}: {FILE_TYPE_FIRST_LINE.join(', ')},&nbsp;
              </p>

              <p className="wf-upload-drag-info-suffix">{FILE_TYPE_SECOND_LINE.join(', ')}</p>
            </div>
          </div>
        </div>
      </Upload>

      {/* Upload file errors */}
      {fileUploadErrors.length > 0 && (
        <div className="wf-upload-error-container">
          {/* File upload failed title */}
          <>
            <WfIcon className="icon_danger" name="danger" type="danger" />
            <p className="wf-upload-error-title">{t('webform.upload.invalid.title')}</p>
            <WfIcon className="icon_close" name="close" type="danger" onClick={handleErrorPanelClose} />
          </>

          <p className="wf-upload-error-description">{t('webform.upload.invalid.description')}</p>

          <div className="wf-upload-error-file-wrap">
            {fileUploadErrors.map(({ name, extension }, index) => (
              <div key={index} className="wf-upload-error-file" title={name + extension}>
                <span className="wf-upload-error-file-name">{name}</span>
                <span className="wf-upload-error-file-extension">{extension}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload file list */}
      <div className="wf-upload-file-container">
        {uploadedFiles.map((file) => (
          <div className={`wf-upload-file-wrap ${file.status === 'Rejected' ? 'error' : ''}`}>
            {/* Status icon */}
            <WfIcon className="icon_left" name={FileStatusEnum[file.status]} />

            {/* File name */}
            <Tooltip
              overlayClassName={`wf-upload-tooltip ${file.status === 'Rejected' ? 'danger' : 'light'}`}
              overlayStyle={{ display: file.nameTooltipShow ? 'inline-block' : 'none' }}
              overlay={file.status === 'Rejected' ? t('webform.upload.try_again') : `${file.name}${file.extension}`}
              placement={file.status === 'Rejected' ? 'top' : 'bottom'}
              trigger="hover"
              onVisibleChange={(visible) => {
                setUploadedFiles((prev) => {
                  const newUploadFiles = [...prev];
                  const newFile = newUploadFiles.filter((f) => f.uid === file.uid)[0];
                  if (newFile) newFile.nameTooltipShow = visible;
                  return newUploadFiles;
                });
              }}
              getTooltipContainer={() => {
                const wfContainer = document.getElementsByClassName('wf-container');
                return wfContainer[0].parentElement!;
              }}
              showArrow={file.status === 'Rejected'}
            >
              <p className="wf-upload-file" onClick={() => hanldeFileClick(file)}>
                <span className="wf-upload-file-name">{file.name}</span>
                <span className="wf-upload-file-extension">{file.extension}</span>
              </p>
            </Tooltip>

            {/* Delete button */}
            <Tooltip
              overlayClassName="wf-upload-tooltip dark"
              overlayStyle={{ display: file.deleteTooltipShow ? 'inline-block' : 'none' }}
              overlay={t('webform.delete')}
              placement="top"
              trigger="hover"
              onVisibleChange={(visible) => {
                setUploadedFiles((prev) => {
                  const newUploadFiles = [...prev];
                  const newFile = newUploadFiles.filter((f) => f.uid === file.uid)[0];
                  if (newFile) newFile.deleteTooltipShow = visible;
                  return newUploadFiles;
                });
              }}
              getTooltipContainer={() => {
                const wfContainer = document.getElementsByClassName('wf-container');
                return wfContainer[0].parentElement!;
              }}
            >
              <WfIcon className="icon_right" name="close" onClick={() => handleRemoveFile(file)} />
            </Tooltip>

            {/* Progress bar */}
            {!!file.percent && file.percent < 100 && (
              <div className="wf-upload-file-progress-bar">
                <div
                  className="wf-upload-file-progress-inner-bar"
                  style={{ width: String(file.percent || 0) + '%' }}
                ></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default WfUpload;
